import { useState } from "react";
import Dropdown from "@/components/Dropdown";
import { useAuth } from "@/lib/auth-context";
import { submitFeedback } from "@/lib/api";

const TYPES = [
  { value: "bug", label: "Bug report" },
  { value: "suggestion", label: "Suggestion" },
  { value: "general", label: "General feedback" },
  { value: "other", label: "Other" },
];

export default function FeedbackButton() {
  const { user, accessToken } = useAuth();
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("general");
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "done" | "error">("idle");

  if (!user) return null;

  function reset() {
    setMessage("");
    setType("general");
    setStatus("idle");
    setOpen(false);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!accessToken || !message.trim()) return;
    setStatus("loading");
    try {
      await submitFeedback({ message: message.trim(), type }, accessToken);
      setStatus("done");
      setTimeout(reset, 2000);
    } catch {
      setStatus("error");
    }
  }

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-16 right-6 z-40 flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium shadow-lg transition-all hover:opacity-80"
        style={{ background: "var(--bg-elev)", borderColor: "var(--border-strong)", color: "var(--text-secondary)" }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
        Feedback
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-end justify-end p-6" onClick={reset}>
          <div
            className="w-full max-w-sm rounded-2xl p-5 shadow-2xl"
            style={{ background: "var(--bg-elev)", border: "1px solid var(--border-strong)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-display text-base font-semibold" style={{ color: "var(--text)" }}>Share feedback</h2>
              <button onClick={reset} className="transition-opacity hover:opacity-60" style={{ color: "var(--text-muted)" }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>

            {status === "done" ? (
              <p className="py-4 text-center text-sm text-green-500">Thanks for your feedback!</p>
            ) : (
              <form onSubmit={handleSubmit} className="flex flex-col gap-3">
                <Dropdown
                  value={type}
                  onChange={setType}
                  options={TYPES}
                  ariaLabel="Feedback type"
                  className="w-full"
                />
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="What's on your mind?"
                  rows={4}
                  maxLength={2000}
                  className="resize-none rounded-lg px-3 py-2 text-sm outline-none focus:border-primary"
                  style={{ background: "var(--bg-subtle)", border: "1px solid var(--border-strong)", color: "var(--text)" }}
                />
                {status === "error" && (
                  <p className="text-xs text-red-500">Something went wrong. Please try again.</p>
                )}
                <button
                  type="submit"
                  disabled={!message.trim() || status === "loading"}
                  className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white shadow-btn transition-all hover:bg-primary-hover disabled:opacity-40"
                >
                  {status === "loading" ? "Sending…" : "Send feedback"}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
